export interface CameraDevice {
  deviceId: string
  label: string
  kind: 'videoinput'
}

export interface CameraPermissionStatus {
  granted: boolean
  denied: boolean
  prompt: boolean
}

// Check camera permission
export async function checkCameraPermission(): Promise<CameraPermissionStatus> {
  try {
    const result = await navigator.permissions.query({ name: 'camera' as PermissionName })
    return {
      granted: result.state === 'granted',
      denied: result.state === 'denied',
      prompt: result.state === 'prompt'
    }
  } catch (error) {
    // Fallback: Try to access camera to check permission
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true })
      stream.getTracks().forEach(track => track.stop())
      return { granted: true, denied: false, prompt: false }
    } catch (err) {
      return { granted: false, denied: true, prompt: false }
    }
  }
}

// Get available cameras
export async function getAvailableCameras(): Promise<CameraDevice[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices
      .filter(device => device.kind === 'videoinput')
      .map(device => ({
        deviceId: device.deviceId,
        label: device.label || `Camera ${device.deviceId.slice(0, 8)}`,
        kind: 'videoinput'
      }))
  } catch (error) {
    console.error('Failed to enumerate cameras:', error)
    return []
  }
}

// Test camera access
export async function testCamera(deviceId?: string): Promise<boolean> {
  try {
    const constraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId } } : true
    }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    stream.getTracks().forEach(track => track.stop())
    return true
  } catch (error) {
    console.error('Camera test failed:', error)
    return false
  }
}
